import type { ColumnDef, EnemyDef, LevelDef, PickupDef, SegmentDef } from '../../types';

// OP-301 Rancho El Búfalo (GDD §10.5, §10.2, issue #119): a Guadalajara morning, written by hand with the format of
// §15.6. 600 u at 6.0 u/s (world 3, level 1): takeoff, warm-up (20 %), tension (30 %) up to CP-301-A at x 300,
// respite (10 %), peak (40 %) and the clandestine airstrip. Gap columns every 8 u with the 3.5 u gap of world 3
// (§5.4), the gap centre never moving more than 3.5 u, all of them with the `w3-silos` skin (#115). The crop dusters
// (EN-31, #116) are the lesson: the first one comes alone after the warm-up, and no other enemy is near it; every
// cloud band leaves 2.0 u free above or below and never sits on a gap when the plane crosses it. The peak adds the
// federales (EN-02) and a hitman plane (EN-03). Rafa Caro Quintal flies his own crop duster after the checkpoint
// (event `rafa-crop-duster`, data only). An armor before the checkpoint and a bribe in the respite; no scapular (it
// goes in OP-302). All x are local.

const column = (x: number, gapY: number): ColumnDef => ({ x, gapY, gapH: 3.5, skin: 'w3-silos' });
const dollar = (x: number, y: number): PickupDef => ({ x, y, type: 'dollar' });
const armor = (x: number, y: number): PickupDef => ({ x, y, type: 'armor' });
const bribe = (x: number, y: number): PickupDef => ({ x, y, type: 'bribe' });
/** A crop duster (EN-31): it flies with the player and sprays its toxic cloud. Kept at a height that leaves 2 u free. */
const duster = (x: number, y: number): EnemyDef => ({ x, y, type: 'EN-31' });
/** A helicopter of the federales (EN-02), hovering high. */
const federales = (x: number, y: number): EnemyDef => ({ x, y, type: 'EN-02' });
/** A hitman plane (EN-03), announced with INTERCEPT. */
const hitman = (x: number, y: number): EnemyDef => ({ x, y, type: 'EN-03' });

const takeoff: SegmentDef = { id: 'w3-bufalo-takeoff-01', kind: 'takeoff', length: 40, columns: [], enemies: [], pickups: [] };

const warmup01: SegmentDef = {
  id: 'w3-bufalo-warmup-01',
  kind: 'pattern',
  phase: 'warmup',
  length: 50,
  columns: [
    column(20.0, 6.0),
    column(28.0, 6.5),
    column(36.0, 6.0),
    column(44.0, 5.5),
  ],
  enemies: [],
  pickups: [
    dollar(23.25, 6.0),
    dollar(24.25, 6.3),
    dollar(25.25, 6.0),
    dollar(39.25, 5.5),
    dollar(40.25, 5.8),
    dollar(41.25, 5.5),
  ],
};

const warmup02: SegmentDef = {
  id: 'w3-bufalo-warmup-02',
  kind: 'pattern',
  phase: 'warmup',
  length: 50,
  columns: [
    column(3.0, 6.0),
    column(11.0, 6.5),
    column(19.0, 6.0),
    column(27.0, 6.5),
    column(35.0, 6.0),
    column(43.0, 5.5),
  ],
  enemies: [],
  pickups: [
    dollar(15.25, 6.0),
    dollar(16.25, 6.3),
    dollar(17.25, 6.0),
    dollar(31.25, 6.5),
    dollar(32.25, 6.8),
    dollar(33.25, 6.5),
  ],
};

const tension01: SegmentDef = {
  id: 'w3-bufalo-tension-01',
  kind: 'pattern',
  phase: 'tension',
  length: 50,
  columns: [
    column(5.0, 6.0),
    column(13.0, 6.0),
    column(21.0, 6.5),
    column(29.0, 6.0),
    column(45.0, 6.0),
  ],
  // The first crop duster comes alone (GDD §10.5): it flies at y 8, so 2.0 u stay free below it.
  enemies: [duster(37.0, 8.0)],
  pickups: [
    dollar(25.25, 6.0),
    dollar(26.25, 6.3),
    dollar(27.25, 6.0),
    dollar(41.25, 6.25),
    dollar(42.25, 6.55),
    dollar(43.25, 6.25),
  ],
};

const tension02: SegmentDef = {
  id: 'w3-bufalo-tension-02',
  kind: 'pattern',
  phase: 'tension',
  length: 50,
  columns: [
    column(4.0, 6.5),
    column(12.0, 6.0),
    column(20.0, 5.5),
    column(28.0, 6.0),
    column(36.0, 6.5),
    column(44.0, 6.0),
  ],
  // A crop duster at y 3 leaves 2.0 u free above it.
  enemies: [duster(16.0, 3.0)],
  pickups: [
    dollar(23.25, 5.75),
    dollar(24.25, 6.05),
    dollar(25.25, 5.75),
    dollar(39.25, 6.25),
    dollar(40.25, 6.55),
    dollar(41.25, 6.25),
  ],
};

const tension03: SegmentDef = {
  id: 'w3-bufalo-tension-03',
  kind: 'pattern',
  phase: 'tension',
  length: 50,
  columns: [
    column(6.0, 6.0),
    column(14.0, 6.5),
    column(22.0, 6.0),
    column(30.0, 6.0),
    column(38.0, 6.5),
    column(46.0, 6.0),
  ],
  enemies: [duster(32.0, 8.0)],
  pickups: [
    dollar(17.25, 6.5),
    dollar(18.25, 6.8),
    dollar(19.25, 6.5),
    dollar(43.25, 6.5),
    dollar(44.25, 6.8),
    dollar(45.25, 6.5),
  ],
};

const respite01: SegmentDef = {
  id: 'w3-bufalo-respite-01',
  kind: 'pattern',
  phase: 'respite',
  length: 50,
  columns: [
    column(5.0, 6.0),
    column(13.0, 6.5),
    column(21.0, 6.0),
    column(29.0, 6.5),
    column(37.0, 6.0),
    column(45.0, 6.5),
  ],
  enemies: [],
  pickups: [
    dollar(17.25, 6.0),
    dollar(18.25, 6.3),
    dollar(19.25, 6.0),
    dollar(33.25, 6.5),
    dollar(34.25, 6.8),
    dollar(35.25, 6.5),
    bribe(9.25, 6.25),
  ],
};

const peak01: SegmentDef = {
  id: 'w3-bufalo-peak-01',
  kind: 'pattern',
  phase: 'peak',
  length: 67,
  columns: [
    column(6.0, 6.0),
    column(14.0, 6.5),
    column(22.0, 6.0),
    column(30.0, 5.5),
    column(38.0, 6.0),
    column(46.0, 6.5),
    column(54.0, 6.0),
    column(62.0, 6.5),
  ],
  enemies: [duster(28.0, 8.0), duster(50.0, 3.0)],
  pickups: [
    dollar(17.25, 6.25),
    dollar(18.25, 6.55),
    dollar(19.25, 6.25),
    dollar(43.25, 6.25),
    dollar(44.25, 6.55),
    dollar(45.25, 6.25),
  ],
};

const peak02: SegmentDef = {
  id: 'w3-bufalo-peak-02',
  kind: 'pattern',
  phase: 'peak',
  length: 67,
  columns: [
    column(5.0, 6.0),
    column(13.0, 6.0),
    column(21.0, 6.5),
    column(29.0, 6.0),
    column(37.0, 6.0),
    column(45.0, 6.5),
    column(53.0, 6.0),
    column(61.0, 6.0),
  ],
  enemies: [federales(24.0, 11.0), duster(46.0, 8.0)],
  pickups: [
    dollar(16.25, 6.25),
    dollar(17.25, 6.55),
    dollar(18.25, 6.25),
    dollar(42.25, 6.25),
    dollar(43.25, 6.55),
    dollar(44.25, 6.25),
  ],
};

const peak03: SegmentDef = {
  id: 'w3-bufalo-peak-03',
  kind: 'pattern',
  phase: 'peak',
  length: 66,
  columns: [
    column(4.0, 6.0),
    column(12.0, 6.5),
    column(20.0, 6.0),
    column(28.0, 6.0),
    column(36.0, 6.5),
    column(44.0, 6.0),
    column(52.0, 6.0),
    column(60.0, 6.5),
  ],
  enemies: [hitman(30.0, 11.0), federales(52.0, 11.0)],
  pickups: [
    dollar(15.25, 6.25),
    dollar(16.25, 6.55),
    dollar(17.25, 6.25),
    dollar(39.25, 6.25),
    dollar(40.25, 6.55),
    dollar(41.25, 6.25),
    armor(24.25, 6.25),
  ],
};

const airstrip: SegmentDef = { id: 'w3-bufalo-airstrip-01', kind: 'airstrip', length: 60, columns: [], enemies: [], pickups: [] };

export const OP_301_SEGMENTS: readonly SegmentDef[] = [takeoff, warmup01, warmup02, tension01, tension02, tension03, respite01, peak01, peak02, peak03, airstrip];

export const OP_301: LevelDef = {
  id: '3-1',
  op: 'OP-301',
  name: 'Rancho El Búfalo',
  world: 3,
  scrollSpeed: 6.0,
  timeOfDay: 'dawn',
  segments: OP_301_SEGMENTS.map((segment) => segment.id),
  checkpoints: [{ id: 'CP-301-A', x: 300 }],
  ending: 'airstrip',
  // Rafa Caro Quintal flies his own crop duster from x 310 to 400 (GDD §10.5, #119): data only; the runner that
  // shows the radio line and runs the crossings comes later (@gdd-pending).
  events: [{ id: 'rafa-crop-duster', fromX: 310, toX: 400 }],
};
